import { NextSurveyItemType, QuestionnaireStatus, UseSetupCallbackCB } from '@/components/decision-tree';
import { TOAST_DEFAULT_OPTIONS } from '@/constants/toast';
import useAnalyticsCalls from '@/hooks/use-analytics';
import useVisitorSession from '@/hooks/useVisitorSession';
import { useGetPpraSurvey, useSavePpraSurveyAnswer } from '@/queries/ppra.queries';
import { SessionSurveyDto } from '@/repositories/ppra.repository';
import { useToast } from '@cmpsr/components';
import { useCallback, useEffect, useRef, useState } from 'react';

export enum SurveyStatus {
  ready = 'ready',
  completed = 'completed',
}

export interface UseSurveyControllerOptions {
  surveyVersion: string;
  onModalViewed?: (surveyItemId: string) => void;
}

export default function useSurveyController({ surveyVersion, onModalViewed }: UseSurveyControllerOptions) {
  const toast = useToast();
  const { callPpraModalViewed } = useAnalyticsCalls();

  const [status, setStatus] = useState(SurveyStatus.ready);
  const [surveyData, setSurveyData] = useState<SessionSurveyDto | null>(null);

  // Get visitor session
  const { sessionToken, initializeSession, isInitializingSession } = useVisitorSession();
  // Get the survey data
  const { data: initialSurveyData, error: questionnaireError } = useGetPpraSurvey(surveyVersion, {
    enabled: !!sessionToken,
    staleTime: Infinity, // Data will never become stale on tab focus
  });

  // Mutations
  const saveSurveyAnswerMutation = useSavePpraSurveyAnswer();

  // Initialize the session
  useEffect(() => {
    if (!sessionToken && !isInitializingSession) {
      initializeSession();
    }
  }, [sessionToken, initializeSession, isInitializingSession]);

  // Set the initial survey data
  useEffect(() => {
    if (initialSurveyData) {
      setSurveyData(initialSurveyData);

      // If the survey initial data is already completed, set the status to completed
      if (initialSurveyData.status === QuestionnaireStatus.completed) {
        setStatus(SurveyStatus.completed);
      }
    }
  }, [initialSurveyData]);

  // Error handling
  const error = questionnaireError || saveSurveyAnswerMutation.error;

  // Helper function to check if this is the last survey item
  const isLastSurveyItem = (survey: SessionSurveyDto): boolean => {
    return !survey.nextSurveyItem || !survey.nextSurveyItem.surveyItemId;
  };

  const onItemViewed = useCallback(
    (surveyItem: NextSurveyItemType) => {
      const lastViewedId = lastViewedItemId.current;
      if (lastViewedId !== surveyItem.surveyItemId) {
        if (onModalViewed) {
          onModalViewed(surveyItem.surveyItemId);
        } else {
          callPpraModalViewed(surveyItem.surveyItemId);
        }
        lastViewedItemId.current = surveyItem.surveyItemId;
      }
    },
    [callPpraModalViewed, onModalViewed]
  );

  const lastViewedItemId = useRef<string | null>(null);

  // Save the answer to the survey
  const decisionTreeCallback: UseSetupCallbackCB = useCallback(
    async (surveyItemId, answer) => {
      if (!answer) return surveyData;

      if (!surveyData) {
        console.error('No survey data available');
        return null;
      }

      try {
        const result = await saveSurveyAnswerMutation.mutateAsync({
          sessionSurveyId: surveyData.id,
          surveyItemId,
          answer,
        });

        setSurveyData(result);

        // If this was the last question, move to completed state
        if (isLastSurveyItem(result)) {
          setStatus(SurveyStatus.completed);
          return null;
        }

        // Return the updated questionnaire
        return result;
      } catch (err) {
        console.error('Failed to save answer:', err);
        toast({
          ...TOAST_DEFAULT_OPTIONS,
          title: 'Error',
          description: 'Failed to save answer',
          status: 'error',
        });
        return surveyData;
      }
    },
    [surveyData, saveSurveyAnswerMutation, toast]
  );

  const backOnFirstSurveyItem = () => {
    window.history.back();
  };

  return {
    status,
    userQuestionnaire: surveyData,
    error,
    decisionTreeCallback,
    backOnFirstSurveyItem,
    onItemViewed,
  };
}
